import api from '@/lib/api';
import type { User, UserRole } from '@pms/shared';

// ─── Interfaces ──────────────────────────────────────────────────────────────

export interface InviteUserPayload {
  /** Optional now — a username-only staff member (Hotel Groups) has no email. */
  email?: string;
  /** Per-tenant login handle for email-less staff. Email OR username required. */
  username?: string;
  role: UserRole;
  firstName?: string;
  lastName?: string;
  /** Empty/omitted ⇒ all properties in the tenant (scope default). */
  propertyIds?: string[];
}

export interface UpdateUserPayload {
  role?: UserRole;
  isActive?: boolean;
  firstName?: string;
  lastName?: string;
}

export interface InviteUserResponse {
  user: User;
  temporaryPassword: string;
  /** The group code (tenant slug) the user logs in with; null if unavailable. */
  groupCode: string | null;
}

export interface UserProperty {
  id: string;
  name: string;
}

// ─── Users API ──────────────────────────────────────────────────────────────

export async function fetchUsers(): Promise<User[]> {
  const { data } = await api.get<User[]>('/users');
  return data;
}

export async function inviteUser(payload: InviteUserPayload): Promise<InviteUserResponse> {
  const { data } = await api.post<InviteUserResponse>('/users/invite', payload);
  return data;
}

export async function updateUser(id: string, payload: UpdateUserPayload): Promise<User> {
  const { data } = await api.patch<User>(`/users/${id}`, payload);
  return data;
}

export async function deactivateUser(id: string): Promise<void> {
  await api.delete(`/users/${id}`);
}

export interface ResetPasswordResponse {
  user: User;
  temporaryPassword: string;
  /** The group code (tenant slug) the user logs in with; null if unavailable. */
  groupCode: string | null;
}

/** Re-issue a one-time temp password (admin, same tenant). */
export async function resetPassword(
  id: string,
): Promise<ResetPasswordResponse> {
  const { data } = await api.post<ResetPasswordResponse>(
    `/users/${id}/reset-password`,
    {},
  );
  return data;
}

export async function fetchUserProperties(userId: string): Promise<UserProperty[]> {
  const { data } = await api.get<UserProperty[]>(`/users/${userId}/properties`);
  return data;
}

export async function assignUserProperties(userId: string, propertyIds: string[]): Promise<void> {
  await api.post(`/users/${userId}/properties`, { propertyIds });
}
